import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PowerOff, Power, Loader2, Radio, Video, RefreshCw,
  Building2, MapPin, Calendar, LayoutGrid, List,
} from 'lucide-react';
import { supabase } from '@/services/supabase';
import { enableCamera } from '@/services/cameras';
import { enablePanel } from '@/services/alarms';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PermissionGate } from '@/components/PermissionGate';
import { cn } from '@/lib/utils';

interface InactiveDevice {
  id: string;
  name: string;
  status: string;
  is_disabled: boolean;
  disabled_at: string | null;
  disabled_reason: string | null;
  site_id: string | null;
  site_name?: string | null;
  account_name?: string | null;
}

let _cachedInactiveCameras: InactiveDevice[] | null = null;
let _cachedInactivePanels: InactiveDevice[] | null = null;

export function DevicesInactivePage() {
  const [cameras, setCameras] = useState<InactiveDevice[]>(_cachedInactiveCameras ?? []);
  const [panels, setPanels] = useState<InactiveDevice[]>(_cachedInactivePanels ?? []);
  const [loading, setLoading] = useState(_cachedInactiveCameras === null);
  const [enablingId, setEnablingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('cameras');
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');

  async function load() {
    if (_cachedInactiveCameras === null) setLoading(true);
    try {
      const [{ data: c }, { data: p }] = await Promise.all([
        supabase
          .from('cameras')
          .select(`
            id, name, status, is_disabled, disabled_at, disabled_reason, site_id,
            site:alarm_sites(name, account:accounts(name))
          `)
          .eq('is_disabled', true)
          .order('disabled_at', { ascending: false }),
        supabase
          .from('alarm_panels')
          .select(`
            id, name, status, is_disabled, disabled_at, disabled_reason, site_id,
            site:alarm_sites(name, account:accounts(name))
          `)
          .eq('is_disabled', true)
          .order('disabled_at', { ascending: false }),
      ]);

      const mapDevice = (d: any): InactiveDevice => ({
        id: d.id,
        name: d.name,
        status: d.status,
        is_disabled: d.is_disabled,
        disabled_at: d.disabled_at,
        disabled_reason: d.disabled_reason,
        site_id: d.site_id,
        site_name: d.site?.name ?? null,
        account_name: d.site?.account?.name ?? null,
      });

      const mappedCameras = (c ?? []).map(mapDevice);
      const mappedPanels = (p ?? []).map(mapDevice);
      _cachedInactiveCameras = mappedCameras;
      _cachedInactivePanels = mappedPanels;
      setCameras(mappedCameras);
      setPanels(mappedPanels);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleEnable(type: 'camera' | 'panel', device: InactiveDevice) {
    setEnablingId(device.id);
    try {
      const opts = { deviceName: device.name, siteId: device.site_id };
      if (type === 'camera') {
        await enableCamera(device.id, opts);
      } else {
        await enablePanel(device.id, opts);
      }
      await load();
    } finally {
      setEnablingId(null);
    }
  }

  function DeviceList({ items, type }: { items: InactiveDevice[]; type: 'camera' | 'panel' }) {
    if (loading) {
      return (
        <div className="flex justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <Card>
          <CardContent className="py-10 text-center">
            <PowerOff className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-30" />
            <p className="text-sm text-muted-foreground">No hay dispositivos desactivados.</p>
          </CardContent>
        </Card>
      );
    }
    if (viewMode === 'list') {
      return (
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 font-medium text-muted-foreground">Dispositivo</th>
                <th className="text-left py-2 px-3 font-medium text-muted-foreground">Cuenta / Sitio</th>
                <th className="text-left py-2 px-3 font-medium text-muted-foreground">Estado</th>
                <th className="text-left py-2 px-3 font-medium text-muted-foreground">Desactivado</th>
                <th className="text-left py-2 px-3 font-medium text-muted-foreground">Razón</th>
                <th className="py-2 px-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                  <td className="py-2 px-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded bg-red-500/10 flex items-center justify-center shrink-0">
                        {type === 'camera' ? <Video className="w-3.5 h-3.5 text-red-500" /> : <Radio className="w-3.5 h-3.5 text-red-500" />}
                      </div>
                      <span className="font-medium">{d.name}</span>
                    </div>
                  </td>
                  <td className="py-2 px-3 text-muted-foreground">
                    <div className="flex items-center gap-1 flex-wrap">
                      {d.account_name && <span className="flex items-center gap-0.5"><Building2 className="w-3 h-3" />{d.account_name}</span>}
                      {d.site_name && <span className="flex items-center gap-0.5 ml-1"><MapPin className="w-3 h-3" />{d.site_id ? <Link to={`/sites/${d.site_id}`} className="hover:underline text-primary">{d.site_name}</Link> : d.site_name}</span>}
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <Badge className={cn('text-xs', d.status === 'ONLINE' ? 'bg-green-500/10 text-green-600' : 'bg-gray-500/10 text-gray-500')}>{d.status}</Badge>
                  </td>
                  <td className="py-2 px-3 text-muted-foreground text-xs">
                    {d.disabled_at ? new Date(d.disabled_at).toLocaleString('es-GT', { timeZone: 'America/Guatemala', dateStyle: 'short', timeStyle: 'short' }) : '—'}
                  </td>
                  <td className="py-2 px-3 text-xs text-orange-600 italic">{d.disabled_reason ?? '—'}</td>
                  <td className="py-2 px-3">
                    <PermissionGate require="cameras.manage">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-green-600 border-green-300 hover:bg-green-500/10"
                        disabled={enablingId === d.id}
                        onClick={() => handleEnable(type, d)}
                      >
                        {enablingId === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Power className="w-3.5 h-3.5 mr-1" />}
                        Reactivar
                      </Button>
                    </PermissionGate>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return (
      <div className="space-y-2">
        {items.map((d) => (
          <div
            key={d.id}
            className="flex items-start gap-3 px-4 py-3 rounded-xl border border-border bg-card"
          >
            <div className="w-9 h-9 rounded-lg bg-red-500/10 flex items-center justify-center shrink-0 mt-0.5">
              {type === 'camera'
                ? <Video className="w-4 h-4 text-red-500" />
                : <Radio className="w-4 h-4 text-red-500" />
              }
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-sm text-foreground">{d.name}</span>
                <Badge className="text-xs bg-red-500/10 text-red-600">Desactivado</Badge>
                <Badge className={cn('text-xs', d.status === 'ONLINE' ? 'bg-green-500/10 text-green-600' : 'bg-gray-500/10 text-gray-500')}>
                  {d.status}
                </Badge>
              </div>
              {d.account_name && (
                <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
                  <Building2 className="w-3 h-3" />
                  {d.account_name}
                  {d.site_id && d.site_name && (
                    <>
                      <span className="mx-1">·</span>
                      <MapPin className="w-3 h-3" />
                      <Link to={`/sites/${d.site_id}`} className="hover:underline text-primary">
                        {d.site_name}
                      </Link>
                    </>
                  )}
                </div>
              )}
              {d.disabled_reason && (
                <p className="text-xs text-orange-600 dark:text-orange-400 mt-0.5 italic">{d.disabled_reason}</p>
              )}
              {d.disabled_at && (
                <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
                  <Calendar className="w-3 h-3" />
                  Desactivado {new Date(d.disabled_at).toLocaleString('es-GT', { timeZone: 'America/Guatemala', dateStyle: 'medium', timeStyle: 'short' })}
                </div>
              )}
            </div>
            <PermissionGate require="cameras.manage">
              <Button
                variant="outline"
                size="sm"
                className="text-green-600 border-green-300 hover:bg-green-500/10 shrink-0"
                disabled={enablingId === d.id}
                onClick={() => handleEnable(type, d)}
              >
                {enablingId === d.id
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Power className="w-3.5 h-3.5 mr-1" />
                }
                Reactivar
              </Button>
            </PermissionGate>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-foreground">Dispositivos dados de baja</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Cámaras y paneles desactivados por falta de pago u otras razones.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={cn('w-4 h-4 mr-1', loading && 'animate-spin')} />
          Actualizar
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center gap-3">
          <TabsList>
            <TabsTrigger value="cameras">
              Cámaras <Badge variant="secondary" className="ml-1">{cameras.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="panels">
              Paneles <Badge variant="secondary" className="ml-1">{panels.length}</Badge>
            </TabsTrigger>
          </TabsList>
          <div className="flex items-center border border-border rounded-md overflow-hidden">
            <button
              onClick={() => setViewMode('card')}
              className={cn('p-2 transition-colors', viewMode === 'card' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}
              title="Vista de tarjetas"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn('p-2 transition-colors', viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}
              title="Vista de lista"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
        <TabsContent value="cameras" className="mt-4">
          <DeviceList items={cameras} type="camera" />
        </TabsContent>
        <TabsContent value="panels" className="mt-4">
          <DeviceList items={panels} type="panel" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
